/**
 * The wall's one native scroll container: its size, and which cards are
 * mounted. The mounted range is recomputed at most once per animation frame
 * and only re-renders when it changes. When the layout changes while the
 * wall is scrolled, the topmost card in view keeps its place on screen
 * (scroll anchoring); at the very top, arrivals push the wall down instead.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { sameIds } from "@/lib/same-ids";
import type { WallLayout } from "./wall-layout";
import { mountedCards, wallOverscan } from "./wall-visible-cards";

export interface WallViewportSize {
  width: number;
  height: number;
}

interface ScrollAnchor {
  id: string;
  y: number;
}

const NO_CARDS: readonly string[] = [];
const NO_SIZE: WallViewportSize = { width: 0, height: 0 };

/**
 * Cards still mounted keep their place in the list, so React never moves
 * their DOM nodes (a move would drop focus and restart animations); newly
 * mounted cards follow in reading order.
 */
function stableOrder(
  previous: readonly string[],
  next: readonly string[],
): readonly string[] {
  const wanted = new Set(next);
  const kept = previous.filter((id) => wanted.has(id));
  const keptIds = new Set(kept);
  return [...kept, ...next.filter((id) => !keptIds.has(id))];
}

/** The topmost mounted card starting in view (else the first in view). */
function anchorFor(
  layout: WallLayout,
  mounted: readonly string[],
  viewTop: number,
): ScrollAnchor | null {
  let anchor: ScrollAnchor | null = null;
  for (const id of mounted) {
    const box = layout.boxes.get(id);
    if (!box || box.y + box.height <= viewTop) continue;
    if (box.y >= viewTop) return { id, y: box.y };
    anchor ??= { id, y: box.y };
  }
  return anchor;
}

/** The scroll container's size, followed with a ResizeObserver. */
export function useWallViewportSize(
  scrollRef: RefObject<HTMLElement | null>,
): WallViewportSize {
  const [size, setSize] = useState(NO_SIZE);
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const apply = (width: number, height: number) =>
      setSize((current) =>
        current.width === width && current.height === height
          ? current
          : { width, height },
      );
    apply(scroller.clientWidth, scroller.clientHeight);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) apply(entry.contentRect.width, entry.contentRect.height);
    });
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [scrollRef]);
  return size;
}

/**
 * The cards to mount for the current scroll position. `canvasRef` is the
 * masonry's box inside the scroll container (content may sit above it).
 * `onScroll` hears every scroll (and layout) once the range is computed.
 */
export function useWallViewport(
  scrollRef: RefObject<HTMLElement | null>,
  canvasRef: RefObject<HTMLElement | null>,
  layout: WallLayout | null,
  viewportHeight: number,
  onScroll?: () => void,
): readonly string[] {
  const [mounted, setMounted] = useState(NO_CARDS);
  const layoutRef = useRef(layout);
  const heightRef = useRef(viewportHeight);
  const anchorRef = useRef<ScrollAnchor | null>(null);
  const frameRef = useRef(0);
  const onScrollRef = useRef(onScroll);
  useLayoutEffect(() => {
    onScrollRef.current = onScroll;
  }, [onScroll]);

  const update = useCallback(() => {
    const scroller = scrollRef.current;
    const canvas = canvasRef.current;
    const current = layoutRef.current;
    if (!scroller || !canvas || !current) {
      anchorRef.current = null;
      setMounted(NO_CARDS);
      onScrollRef.current?.();
      return;
    }
    const viewTop = scroller.scrollTop - canvas.offsetTop;
    const viewBottom = viewTop + heightRef.current;
    const overscan = wallOverscan(current.geometry.columns, heightRef.current);
    const next = mountedCards(current, viewTop, viewBottom, overscan);
    anchorRef.current =
      scroller.scrollTop > 0 ? anchorFor(current, next, viewTop) : null;
    onScrollRef.current?.();
    setMounted((previous) => {
      const stable = stableOrder(previous, next);
      return sameIds(previous, stable) ? previous : stable;
    });
  }, [canvasRef, scrollRef]);

  useLayoutEffect(() => {
    layoutRef.current = layout;
    heightRef.current = viewportHeight;
    const scroller = scrollRef.current;
    const anchor = anchorRef.current;
    const moved = anchor ? layout?.boxes.get(anchor.id) : undefined;
    if (scroller && anchor && moved && moved.y !== anchor.y) {
      scroller.scrollTop += moved.y - anchor.y;
    }
    update();
  }, [layout, scrollRef, update, viewportHeight]);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const onScroll = () => {
      if (frameRef.current) return;
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = 0;
        update();
      });
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frameRef.current);
      frameRef.current = 0;
    };
  }, [scrollRef, update]);

  return mounted;
}
